import { useState, useEffect } from "react";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import { FaExclamationCircle } from "react-icons/fa";
import ModalButton from "../Ui/Buttons/ModalButtons";
import style from "../EmailCheck/emailCheck.module.css";
import ModalComponent from "../Ui/ModalComponent/ModalWindow";
import { forgotPassword, verifyResetCode, resetPassword } from "../../common/api/authApi";
import React from "react";

const steps = [
  "Восстановление пароля",
  "Код подтверждения",
  "Новый пароль"
];

interface PasswordRecoveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  setErrorModal: (error: any) => void;
}

const PasswordRecoveryModal: React.FC<PasswordRecoveryModalProps> = ({ isOpen, onClose, setErrorModal }) => {
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [timeLeft, setTimeLeft] = useState(300);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep(0);
      setEmail("");
      setTimeLeft(300);
      setFailedAttempts(0);
      return;
    }
    if (step === 1) {
      setTimeLeft(300);
      const interval = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen, step]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      disableBackdropClick={true}
      headerContent={
        <div className="flex justify-between items-center w-full">
          <div className="flex flex-1 items-center gap-2">
            {steps.map((label, idx) => (
              <>
                <div key={label} className={`flex flex-col items-center ${idx === step ? "font-bold text-black" : "text-gray-400"}`}>
                  <div className={`rounded-full border w-6 h-6 flex items-center justify-center ${idx === step ? "bg-mainColor text-white" : "bg-gray-200"}`}>{idx + 1}</div>
                  <span className="text-xs mt-1">{label}</span>
                </div>
                {idx < steps.length - 1 && <div className="w-8 h-px bg-gray-300 mx-2" />}
              </>
            ))}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors ml-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      }
      content={
        <Formik
          initialValues={{
            email: "",
            code: "",
            password: "",
            confirmPassword: ""
          }}
          validationSchema={
            step === 0
              ? Yup.object({
                email: Yup.string().email("Введите корректный e-mail").required("Обязательное поле")
              })
              : step === 1
                ? Yup.object({
                  code: Yup.string().required("Введите код из письма")
                })
                : Yup.object({
                  password: Yup.string().min(6, "Минимум 6 символов").required("Обязательное поле"),
                  confirmPassword: Yup.string()
                    .oneOf([Yup.ref("password")], "Пароли должны совпадать")
                    .required("Обязательное поле")
                })
          }
          onSubmit={async (values, actions) => {
            setIsLoading(true);
            try {
              if (step === 0) {
                const resp = await forgotPassword(values.email);
                if (resp.status === 1) {
                  setEmail(values.email);
                  setStep(1);
                } else {
                  actions.setFieldError("email", resp.message || "Ошибка отправки кода");
                }
              } else if (step === 1) {
                const resp = await verifyResetCode(email, values.code);
                if (resp.status === 1) {
                  setStep(2);
                } else {
                  actions.setFieldError("code", resp.message || "Неверный код");
                  setFailedAttempts((prev) => prev + 1);
                  if (failedAttempts + 1 >= 3) {
                    setErrorModal({ hasError: true, errorMessage: "Превышено количество попыток. Попробуйте снова." });
                    onClose();
                  }
                }
              } else if (step === 2) {
                const resp = await resetPassword(email, values.code, values.password, values.confirmPassword);
                if (resp.status === 1) {
                  setStep(0);
                  onClose();
                } else {
                  actions.setFieldError("password", resp.message || "Ошибка смены пароля");
                }
              }
            } catch (error: any) {
              setErrorModal({ hasError: true, errorMessage: error?.message || "Ошибка" });
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <Form className="flex flex-col gap-4 mt-5">
              {step === 0 && (
                <div>
                  <label className="block mb-2">Укажите адрес e-mail для отправки кода сброса пароля</label>
                  <Field
                    name="email"
                    type="email"
                    placeholder="Введите адрес e-mail"
                    className={`w-full p-2 border rounded ${touched.email && errors.email ? "border-red-500" : ""}`}
                  />
                  {touched.email && errors.email && (
                    <div className="text-red-500 text-sm mt-1 flex items-center gap-1"><FaExclamationCircle />{errors.email}</div>
                  )}
                </div>
              )}
              {step === 1 && (
                <div>
                  <label className="block mb-2">Введите код подтверждения из Email сообщения, отправленного на {email}</label>
                  <Field
                    name="code"
                    placeholder="Введите код"
                    className={`w-full p-2 border rounded ${touched.code && errors.code ? "border-red-500" : ""}`}
                    disabled={timeLeft === 0}
                  />
                  {touched.code && errors.code && (
                    <div className="text-red-500 text-sm mt-1 flex items-center gap-1"><FaExclamationCircle />{errors.code}</div>
                  )}
                  <div className="text-gray-500 text-sm mt-2">Повторный запрос кода через {formatTime(timeLeft)}</div>
                  <div className="text-gray-500 text-sm">Осталось попыток: {3 - failedAttempts}</div>
                </div>
              )}
              {step === 2 && (
                <>
                  <div>
                    <label className="block mb-2">Новый пароль</label>
                    <Field
                      name="password"
                      type="password"
                      placeholder="Введите новый пароль"
                      className={`w-full p-2 border rounded ${touched.password && errors.password ? "border-red-500" : ""}`}
                    />
                    {touched.password && errors.password && (
                      <div className="text-red-500 text-sm mt-1 flex items-center gap-1"><FaExclamationCircle />{errors.password}</div>
                    )}
                  </div>
                  <div>
                    <label className="block mb-2">Повторите новый пароль</label>
                    <Field
                      name="confirmPassword"
                      type="password"
                      placeholder="Повторите новый пароль"
                      className={`w-full p-2 border rounded ${touched.confirmPassword && errors.confirmPassword ? "border-red-500" : ""}`}
                    />
                    {touched.confirmPassword && errors.confirmPassword && (
                      <div className="text-red-500 text-sm mt-1 flex items-center gap-1"><FaExclamationCircle />{errors.confirmPassword}</div>
                    )}
                  </div>
                </>
              )}
              <button
                type="button"
                className="bg-mainColor  font-semibold py-2 px-4 rounded-md max-w-[180px] self-end"
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    await handleSubmit(); // вручную вызываем сабмит формы
                  } catch (error: any) {
                    setErrorModal({ hasError: true, errorMessage: error?.message || "Ошибка" });
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={Boolean(
                  isLoading ||
                  (step === 0 && (!values.email || errors.email)) ||
                  (step === 1 && (!values.code || errors.code || timeLeft === 0)) ||
                  (step === 2 && (!values.password || !values.confirmPassword || errors.password || errors.confirmPassword))
                )}
              >
                {step === 2 ? "Сохранить" : "Продолжить"}
              </button>
            </Form>
          )}
        </Formik>
      }
    />
  );
};

export default PasswordRecoveryModal; 